import {Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {TriggerConfiguration} from '@models/adminstration/machine/trigger/trigger-configuration';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {TRIGGER_TYPE} from '@constants/trigger/trigger-type.constant';
import {NgbCalendar, NgbDate} from '@ng-bootstrap/ng-bootstrap';

type ChangeCallbackFn<T> = (value: T) => void;
type TouchCallbackFn = () => void;

@Component({
    selector: 'scitis-admin-time-based',
    templateUrl: './time-based.component.html',
    styleUrls: ['./time-based.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TimeBasedComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => TimeBasedComponent),
            multi: true
        }
    ],
    standalone: false
})
export class TimeBasedComponent implements ControlValueAccessor, Validator, OnInit, OnChanges {

  @Input()
  cloudPlugs: Gateway[] = [];

  @Input()
  isLoadingCloudPlugs = false;

  @Input()
  isAdminOrHigher: boolean = false;

  @Input()
  submitted: boolean = false;

  @Input()
  triggerConfiguration: TriggerConfiguration = new TriggerConfiguration();

  @Input()
  selectedTriggerType: TRIGGER_TYPE | string = '';

  @Output()
  formIsValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  updatedTriggerConfiguration: EventEmitter<TriggerConfiguration> = new EventEmitter<TriggerConfiguration>();

  triggerConfigTimeBasedForm: UntypedFormGroup = this.fb.group({});

  REAL_TIME_BASED_INTERVAL_TYPES = ['HOURS', 'DAYS', 'WEEK', 'MONTH', 'YEAR'];

  intervalTypes: any[] = new Array<any>();
  startTime: string = '';
  startDateRequired: string = '';
  startTimeRequired: string = '';
  effectiveStartDate: string = '';
  isDateValid: boolean = false;

  constructor(private fb: UntypedFormBuilder,
              private translate: TranslateService,
              private calendar: NgbCalendar,
              private translateService: TranslateService) {
    this.startTime = this.translateService.instant('Start time');
    this.startDateRequired = this.translateService.instant('Start date required');
    this.startTimeRequired = this.translateService.instant('Start time required');
  }

// convenience getter for easy access to form fields
  get formControls() {
    return this.triggerConfigTimeBasedForm.controls;
  }

  ngOnInit(): void {
    this.generateTriggerConfigTimeBasedForm();
    this.setIntervalTypes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedTriggerType) {
      this.setIntervalTypes();
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.triggerConfigTimeBasedForm.invalid) {
      return {invalidForm: {valid: false, message: 'TimeBasedComponent fields are invalid'}};
    }
    return null;
  }

  writeValue(val: any): void {
    if (val) {
      this.triggerConfigTimeBasedForm.setValue(val, {emitEvent: false});
    }
  }

  registerOnChange(fn: ChangeCallbackFn<object>): void {
    this.triggerConfigTimeBasedForm.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: TouchCallbackFn): void {
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.triggerConfigTimeBasedForm.disable();
    } else {
      this.triggerConfigTimeBasedForm.enable();
    }
  }

  onFocusOut(): void {
    this.onTriggerConfigUpdated();
  }

  onFocusOutDateTime(event): void {
    if (event.time.hour === 'HH' || event.time.minute === 'MM' || event.time.second === 'SS') {
      return;
    }
    this.effectiveStartDate = this.setDateFromInput(event.date, event.time);
  }

  private generateTriggerConfigTimeBasedForm(): void {
    this.triggerConfigTimeBasedForm = this.fb.group({
      cloudPlug: new UntypedFormControl(this.triggerConfiguration.cloudplugId, [Validators.required]),
      interval: new UntypedFormControl(this.triggerConfiguration.triggerInterval, [Validators.required, Validators.min(1)]),
      intervalType: new UntypedFormControl(this.triggerConfiguration.triggerIntervalType, Validators.required),
    });

    this.formIsValid.emit(!this.triggerConfigTimeBasedForm.invalid);
  }

  public getStartDate(startDate: string | null) {
    if (!startDate) {
      return this.calendar.getToday();
    } else {
      return startDate;
    }
  }

  public getStartTimeFromUnixDate(startDate: string | null) {
    if (!startDate) {
      return {hour: 'HH', minute: 'MM', second: 'SS'};
    }

    const date = new Date(startDate);
    return {
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds()
    };
  }

  private onTriggerConfigUpdated() {
    const triggerConfigUpdated = new TriggerConfiguration();
    triggerConfigUpdated.triggerInterval = this.formControls.interval.value;
    triggerConfigUpdated.triggerIntervalType = this.formControls.intervalType.value;
    triggerConfigUpdated.effectiveStartDate = this.effectiveStartDate;
    triggerConfigUpdated.cloudplugId = this.formControls.cloudPlug.value;
    this.updatedTriggerConfiguration.emit(triggerConfigUpdated);
    this.formIsValid.emit(!this.triggerConfigTimeBasedForm.invalid);
  }

  private setDateFromInput(date: any, time: any): string {
    if (time) {
      let newDate;
      if (date instanceof NgbDate) {
        newDate = new Date();
        newDate.setDate(date.day);
        newDate.setMonth(date.month - 1);
        newDate.setFullYear(date.year);
      } else {
        newDate = new Date(+date);
      }
      newDate.setHours(time.hour, time.minute, time.second);
      this.isDateValid = true;
      return String(newDate.getTime());
    }
    this.isDateValid = false;
    return '';
  }

  private setIntervalTypes() {
    if (this.selectedTriggerType === TRIGGER_TYPE.REAL_TIME_BASED) {
      this.setTranslatedIntervalTypes(this.REAL_TIME_BASED_INTERVAL_TYPES);
    }
  }

  private setTranslatedIntervalTypes(intervalTypes: string[]) {
    this.intervalTypes = [];
    for (const type of intervalTypes) {
      this.intervalTypes = [...this.intervalTypes, {
        key: type,
        value: this.translate.instant(type)
      }];
    }
  }
}

